import { useState, useEffect, useCallback, useRef } from "react";
import { useWeb3 } from "../context/Web3Context";
import { getEthersProvider } from "../web3/provider";
import { getPurchaseContract, getScnContract, getUsdtContract } from "../web3/contracts";
import { POLL_INTERVAL_MS } from "../config/app.config";

// Reads all on-chain data the dashboard needs. Polls on an interval
// (NOT contract event callbacks) to avoid re-render loops. Safe to mount once.
export function useUserData() {
  const { account, isConnected, isCorrectNetwork } = useWeb3();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Avoid overlapping reads / setState after unmount.
  const inFlight = useRef(false);
  const mounted = useRef(true);

  const load = useCallback(async () => {
    if (!account || !isCorrectNetwork) {
      setData(null);
      return;
    }
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    try {
      const provider = getEthersProvider();
      const purchase = getPurchaseContract(provider);
      const scn = getScnContract(provider);
      const usdt = getUsdtContract(provider);

      const userId = await purchase.addressToId(account);
      const registered = !userId.isZero();

      const [usdtBal, scnBal, scnPrice, paused] = await Promise.all([
        usdt.balanceOf(account),
        scn.balanceOf(account),
        purchase.getCurrentSCNPrice(),
        purchase.paused(),
      ]);

      let details = null;
      let packages = null;
      let income = null;
      let scnStats = null;
      let packageDetails = null; // per-package: [{isActive, totalInvested, totalEarned, ceilingLimit, remainingCeiling}]
      let totalTeam = null;      // matrix team size for package 1 (whole downline)

      if (registered) {
        const [d, p, inc, st, matrix1] = await Promise.all([
          purchase.getUserDetails(userId),
          purchase.getUserPackages(userId),
          purchase.getUserIncomeBreakdown(userId),
          purchase.getUserSCNStats(userId),
          purchase.getMatrixDetails(userId, 1),
        ]);
        details = d;
        packages = p;
        income = inc;
        scnStats = st;
        totalTeam = matrix1.teamSize;

        // Per-package ceiling / earned details (9 packages).
        packageDetails = await Promise.all(
          Array.from({ length: 9 }, (_, i) => purchase.getUserPackageDetails(userId, i + 1))
        );
      }

      if (!mounted.current) return;
      setData({
        userId,
        registered,
        usdtBal,
        scnBal,
        scnPrice,
        paused,
        details,
        packages,
        income,
        scnStats,
        packageDetails,
        totalTeam,
      });
      setError(null);
    } catch (e) {
      if (mounted.current) setError(e);
    } finally {
      inFlight.current = false;
      if (mounted.current) setLoading(false);
    }
  }, [account, isCorrectNetwork]);

  // Initial load + polling. Re-binds when account/network changes.
  useEffect(() => {
    mounted.current = true;
    if (!isConnected || !isCorrectNetwork) {
      setData(null);
      return;
    }
    load();
    const id = setInterval(load, POLL_INTERVAL_MS);
    return () => {
      mounted.current = false;
      clearInterval(id);
    };
  }, [isConnected, isCorrectNetwork, load]);

  return { data, loading, error, refresh: load };
}
